import type { ClaimResponse } from "@patent-studio/api-types";
import { StatusPill, type PillTone } from "@/components/ui/StatusPill";

/** Who wrote a record: Praxis, Praxis as the attorney changed it, or the attorney. */
export type Origin = ClaimResponse["origin"];

/** The surfaces that tag their records with an author, each in its own words. */
export type OriginSurface = "claim" | "framework" | "term" | "term-history";

const WORDS: Record<
  OriginSurface,
  Record<Origin, { words: string; tone: PillTone }>
> = {
  // v0.28's claim tag: Praxis's muted, an edit blue, the attorney's own in the accent.
  claim: {
    praxis: { words: "Added by Praxis", tone: "neutral" },
    edited: { words: "Edited by you", tone: "info" },
    attorney: { words: "Added by you", tone: "brand" },
  },
  framework: {
    praxis: { words: "Praxis", tone: "brand" },
    edited: { words: "Praxis · edited", tone: "neutral" },
    attorney: { words: "Added by you", tone: "info" },
  },
  // The Terminology panel's: who named a term. A term is named, never edited, so `edited`
  // reads as the attorney's own.
  term: {
    praxis: { words: "Praxis", tone: "brand" },
    edited: { words: "Defined by you", tone: "info" },
    attorney: { words: "Defined by you", tone: "info" },
  },
  // Who gave one of a term's earlier names, as its edit history lists them.
  "term-history": {
    praxis: { words: "Praxis", tone: "brand" },
    edited: { words: "You", tone: "info" },
    attorney: { words: "You", tone: "info" },
  },
};

/** The surfaces whose tag is written in sentence case, as v0.28's Terminology panel draws it. */
const SENTENCE_CASE: ReadonlySet<OriginSurface> = new Set([
  "term",
  "term-history",
]);

/** Who wrote a record, as the surface it sits on says it. */
export function OriginPill({
  origin,
  surface,
}: {
  origin: Origin;
  surface: OriginSurface;
}) {
  const { words, tone } = WORDS[surface][origin];
  return (
    <StatusPill tone={tone} size="sm" plain={SENTENCE_CASE.has(surface)}>
      {words}
    </StatusPill>
  );
}
